import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Search, X } from 'lucide-react';
import { 
  getCategorizedIcons, 
  getIconComponent, 
  searchIcons,
  getBadgeSuitableIcons 
} from '@/utils/iconUtils';

interface BadgeIconPickerProps {
  value?: string;
  onChange: (iconName: string) => void;
  disabled?: boolean;
  className?: string;
}

export const BadgeIconPicker: React.FC<BadgeIconPickerProps> = ({
  value,
  onChange,
  disabled = false,
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('popular');

  // Get categorized icons
  const categorizedIcons = useMemo(() => {
    return getCategorizedIcons();
  }, []);
  
  // Get all badge-suitable icons for search
  const allBadgeIcons = useMemo(() => {
    return getBadgeSuitableIcons();
  }, []);
  
  // Filter icons based on search within the active category
  const filteredIcons = useMemo(() => {
    if (!searchTerm.trim()) {
      return activeTab === 'all' ? allBadgeIcons : categorizedIcons[activeTab as keyof typeof categorizedIcons] || [];
    }
    
    const iconsToSearch = activeTab === 'all' ? allBadgeIcons : categorizedIcons[activeTab as keyof typeof categorizedIcons] || [];
    return searchIcons(iconsToSearch, searchTerm);
  }, [categorizedIcons, allBadgeIcons, searchTerm, activeTab]);

  const handleIconSelect = (iconName: string) => {
    onChange(iconName);
    setIsOpen(false);
    setSearchTerm('');
  };

  const clearSelection = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
  };

  const SelectedIcon = value ? getIconComponent(value) : null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="outline" 
          className={`w-full justify-start ${className}`}
          disabled={disabled}
        >
          {SelectedIcon && value ? (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <SelectedIcon className="h-4 w-4" />
                <span className="truncate">{value}</span>
              </div>
              {!disabled && (
                <X 
                  className="h-4 w-4 hover:bg-muted rounded-sm p-0.5 ml-2" 
                  onClick={clearSelection}
                />
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">
              {disabled ? 'Icon selection disabled' : 'Select an icon...'}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        {/* Search Header */}
        <div className="p-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search icons..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="px-3 pt-2">
            <TabsList className="grid w-full grid-cols-4 text-xs h-8">
              <TabsTrigger value="popular" className="text-xs">Popular</TabsTrigger>
              <TabsTrigger value="badges" className="text-xs">Badges</TabsTrigger>
              <TabsTrigger value="shapes" className="text-xs">Shapes</TabsTrigger>
              <TabsTrigger value="symbols" className="text-xs">Symbols</TabsTrigger>
            </TabsList>
          </div>

          {/* Icons Grid */}
          <ScrollArea className="h-64">
            <div className="p-3">
              <div className="grid grid-cols-8 gap-1">
                {filteredIcons.map((iconName) => {
                  const IconComponent = getIconComponent(iconName);
                  return (
                    <Button
                      key={iconName}
                      variant={value === iconName ? "default" : "ghost"}
                      size="sm"
                      onClick={() => handleIconSelect(iconName)}
                      className="h-10 w-10 p-0 hover:bg-muted"
                      title={iconName}
                    >
                      <IconComponent className="h-4 w-4" />
                    </Button>
                  );
                })}
              </div>
              
              {/* Empty state */}
              {filteredIcons.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No icons found</p>
                  <p className="text-xs">Try a different search term</p>
                </div>
              )}
            </div>
          </ScrollArea>

          {/* Footer with count */}
          <div className="px-3 py-2 border-t bg-muted/30">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{filteredIcons.length} icons available</span>
              {searchTerm && (
                <Badge variant="secondary" className="text-xs">
                  Search: "{searchTerm}"
                </Badge>
              )}
            </div>
          </div>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
};